import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import LoadingButton from '@mui/lab/LoadingButton';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Typography, useTheme } from '@mui/material';
import { IconComponent } from '@zippeditoolsjs/zippedi-icons';

import ContactForm from './ContactForm';
import ContactsTable from './ContactsTable';

export default function Contacts(props) {
  const {
    selectedClient,
    getContacts,
    contacts,
    isLoadingContacts,
    getCWSBrunoUsers,
    cwsBrunoUsers,
    isLoadingCWSBrunoUsers,
    getUserSuppliers,
    suppliers,
    isLoadingSuppliers,
    getUserRoles,
    userRoles,
    isLoadingUserRoles,
    getChains,
    chains,
    isLoadingChains,
    getUserStores,
    userStores,
    isLoadingUserStores,
    getStoreCategories,
    categories,
    isLoadingCategories,
    postContact,
    postContactResponse,
    isLoadingPostContact,
    getContactRoles,
    contactRoles,
    isLoadingContactRoles,
    clients,
    getRegions,
    regions,
    isLoadingRegions,
    getContactNotificationFeatures,
    notificationFeatures,
    isLoadingNotificationFeatures,
    updateBrunoUser,
    updateBrunoUserResponse,
    isLoadingUpdateBrunoUser,
    deleteContact,
    deleteContactResponse,
    isLoadingDeleteContact,
    patchContact,
    patchContactResponse,
    isLoadingPatchContact,
  } = props;
  const theme = useTheme();
  const { t } = useTranslation();
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentContact, setCurrentContact] = useState(null);

  // Lifecycle methods
  useEffect(() => {
    const includeCWSUsers = true;
    const includeCWSProducts = true;
    const includeCWSClients = true;
    const includeTaskManagerUsers = true;
    const includeTaskManagerRoles = true;
    const includeTaskManagerProfiles = true;

    // Get the CWS users and products and Task Manager App (Bruno) users and roles
    getCWSBrunoUsers(includeCWSProducts, includeCWSClients, includeTaskManagerProfiles);

    // Get the suppliers for the current user based on the selected client
    getUserSuppliers(selectedClient?.client_id);

    // Get user stores based on the selected client
    getUserStores(selectedClient?.client_id);

    // Get the contacts list with the CWS products and the Task Manager App (Bruno) roles
    getContacts(includeCWSUsers, includeCWSProducts, includeTaskManagerUsers, includeTaskManagerRoles);

    // Get static data that doesn't depend on the client
    getUserRoles();
    getChains();
    getContactRoles();
    getRegions();
    getContactNotificationFeatures();
  }, [selectedClient?.client_id]);

  // Refreshes the contact list and closes the dialog on a successful save/update action.
  useEffect(() => {
    const shouldRefresh =
      postContactResponse?.message === 'Created' ||
      updateBrunoUserResponse?.message === 'OK' || // Note: This only updates profiles, not the contact itself
      deleteContactResponse?.message === 'OK' ||
      patchContactResponse?.message === 'OK';

    if (shouldRefresh) {
      const includeCWSUsers = true;
      const includeCWSProducts = true;
      const includeCWSClients = true;
      const includeTaskManagerUsers = true;
      const includeTaskManagerRoles = true;
      const includeTaskManagerProfiles = true;

      if (updateBrunoUserResponse?.message === 'OK') {
        // If profiles were updated, we need to refresh the user list that contains them.
        getCWSBrunoUsers(includeCWSProducts, includeCWSClients, includeTaskManagerProfiles);
      }
      if (deleteContactResponse?.message === 'OK') {
        handleCloseDeleteDialog();
      }
      getContacts(includeCWSUsers, includeCWSProducts, includeTaskManagerUsers, includeTaskManagerRoles);
      if (postContactResponse?.message === 'Created' || patchContactResponse?.message === 'OK')
        handleCloseAddDialog();
    }
  }, [postContactResponse, updateBrunoUserResponse, patchContactResponse, deleteContactResponse]);

  // Methods

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setCurrentContact(null);
  }

  const handleEditClick = (contact) => {
    setCurrentContact(contact);
    setOpenAddDialog(true);
  };

  const handleDeleteClick = (contact) => {
    setCurrentContact(contact);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setCurrentContact(null);
  };

  return (
    <Grid container pt={4} px={2} sx={{ overflow: 'hidden', width: '100%' }}>
      <Grid item xs={12}>
        {/* Page title */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant='h4'>{t('cws_app.contacts.Contacts', 'Contacts')}</Typography>
          {/* Add contact button */}
          <Button
            variant='contained'
            startIcon={
              <IconComponent
                iconName={'add'}
                style={{ fontSize: '20px' }}
              />
            }
            onClick={() => setOpenAddDialog(true)}
          >
            {t('cws_app.management.add_contact', 'Add Contact')}
          </Button>
        </Box>
      </Grid>
      <Box sx={{ overflow: 'hidden', mt: 2, minWidth: '90vh', width: '100%' }}>
        <ContactsTable
          t={t}
          theme={theme}
          contacts={contacts}
          isLoadingContacts={isLoadingContacts}
          onEdit={handleEditClick}
          onDelete={handleDeleteClick}
        />
      </Box>
      {/* Add contact dialog */}
      <Dialog
        open={openAddDialog}
        onClose={handleCloseAddDialog}
        maxWidth='md'
        fullWidth
      >
        <DialogContent>
          <ContactForm
            t={t}
            theme={theme}
            selectedClient={selectedClient}
            cwsBrunoUsers={cwsBrunoUsers}
            isLoadingCWSBrunoUsers={isLoadingCWSBrunoUsers}
            suppliers={suppliers}
            isLoadingSuppliers={isLoadingSuppliers}
            userRoles={userRoles}
            isLoadingUserRoles={isLoadingUserRoles}
            chains={chains}
            isLoadingChains={isLoadingChains}
            userStores={userStores}
            isLoadingUserStores={isLoadingUserStores}
            getStoreCategories={getStoreCategories}
            categories={categories}
            isLoadingCategories={isLoadingCategories}
            postContact={postContact}
            isLoadingPostContact={isLoadingPostContact}
            contactRoles={contactRoles}
            isLoadingContactRoles={isLoadingContactRoles}
            clients={clients}
            regions={regions}
            isLoadingRegions={isLoadingRegions}
            notificationFeatures={notificationFeatures}
            isLoadingNotificationFeatures={isLoadingNotificationFeatures}
            updateBrunoUser={updateBrunoUser}
            updateBrunoUserResponse={updateBrunoUserResponse}
            isLoadingUpdateBrunoUser={isLoadingUpdateBrunoUser}
            currentContact={currentContact}
            patchContact={patchContact}
            isLoadingPatchContact={isLoadingPatchContact}
          />
        </DialogContent>
      </Dialog>
      {/* Delete contact dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>{t('cws_app.contacts.delete_title', 'Delete This Contact?')}</DialogTitle>
        <DialogContent>
          <Typography>{t('cws_app.contacts.delete_message', 'This contact will be permanently deleted. This action cannot be undone.')}</Typography>
          {currentContact && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Box><strong>{t('cws_app.contacts.Principal', 'Principal')}:</strong> {currentContact.principal}</Box>
              <Box><strong>{t('cws_app.contact.Level', 'Level')}:</strong> {t(`cws_app.contacts.${currentContact.level}`, currentContact.level)} </Box>
              <Box><strong>{t('cws_app.general.email', 'Email')}:</strong> {currentContact.email ? currentContact.email : '-'}</Box>
              <Box><strong>{t('cws_app.contacts.Phone', 'Phone')}:</strong> {currentContact.phone ? currentContact.phone : '-'}</Box>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>{t('cws_app.general.cancel', 'Cancel')}</Button>
          <LoadingButton
            loading={isLoadingDeleteContact}
            variant="contained"
            color="error"
            onClick={() => deleteContact({ contact_id: currentContact.contact_id })}
          >
            {t('cws_app.general.delete', 'Delete')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Grid>
  )
}
